export const START_TIME_MAPPING = "activity.schedules.0.start_time";
export const END_TIME_MAPPING = "activity.schedules.0.end_time";
export const EXPECTED_PARTICIPANTS_MAPPING = "activity.expected_participants";
export const ACTIVITY_DURATION_MAPPING = "activity.duration";
export const SCHEDULE_DURATION_MAPPING = "activity.schedules.0.duration";

export function isEditableTimeField(field) {
  return (
    field?.field_mapping === START_TIME_MAPPING ||
    field?.field_mapping === END_TIME_MAPPING
  );
}

export function parseTimeToMinutes(value) {
  if (typeof value !== "string") {
    return null;
  }

  const match = value.trim().match(/^(\d{1,2}):([0-5]\d)(?::[0-5]\d)?$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (hours < 0 || hours > 23) {
    return null;
  }

  return hours * 60 + minutes;
}

export function formatTimeFromMinutes(minutes) {
  if (!Number.isFinite(minutes)) {
    return null;
  }

  const maxMinutes = 23 * 60 + 59;
  const safeMinutes = Math.min(Math.max(0, minutes), maxMinutes);
  const hours = Math.floor(safeMinutes / 60);
  const mins = safeMinutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

function parsePositiveWholeNumber(value) {
  if (typeof value === "number") {
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  if (!/^\d+$/.test(normalized)) {
    return null;
  }

  const numberValue = Number(normalized);
  return Number.isSafeInteger(numberValue) && numberValue > 0
    ? numberValue
    : null;
}

function getParticipantDurationInMinutes(activityDurationMinutes, expectedParticipants) {
  const durationMinutes = Number(activityDurationMinutes);
  const participantCount = parsePositiveWholeNumber(expectedParticipants);

  if (
    !Number.isFinite(durationMinutes) ||
    durationMinutes <= 0 ||
    participantCount === null
  ) {
    return null;
  }

  return Math.round(durationMinutes * participantCount);
}

function validateDurationMinutes(durationMinutes, settings = {}) {
  const minDuration = Number(settings?.min_duration);
  const maxDuration = Number(settings?.max_duration);
  const minMinutes = Number.isFinite(minDuration) && minDuration > 0
    ? Math.round(minDuration * 60)
    : null;
  const maxMinutes = Number.isFinite(maxDuration) && maxDuration > 0
    ? Math.round(maxDuration * 60)
    : null;

  if (minMinutes !== null && durationMinutes < minMinutes) {
    return {
      valid: false,
      reason: "min",
      durationMinutes,
      limitHours: minDuration,
      limitMinutes: minMinutes,
    };
  }

  if (maxMinutes !== null && durationMinutes > maxMinutes) {
    return {
      valid: false,
      reason: "max",
      durationMinutes,
      limitHours: maxDuration,
      limitMinutes: maxMinutes,
    };
  }

  return {
    valid: true,
    reason: null,
    durationMinutes,
  };
}

export function getParticipantBasedEndTime(
  startTime,
  activityDurationMinutes,
  expectedParticipants,
) {
  const startMinutes = parseTimeToMinutes(startTime);
  const participantDurationMinutes = getParticipantDurationInMinutes(
    activityDurationMinutes,
    expectedParticipants,
  );

  if (
    startMinutes === null ||
    participantDurationMinutes === null
  ) {
    return null;
  }

  // Clamp to the end of the day rather than silently no-op: a user who entered
  // a participant count must see *something* happen (ADR-0007, boundary policy).
  const endMinutes = startMinutes + participantDurationMinutes;
  return formatTimeFromMinutes(endMinutes);
}

// Computes the end-time written by the participant → end-time synchronisation
// and validates the requested duration, before the display end time is clamped
// to 23:59. This makes overlong schedules rejectable instead of looking valid
// merely because their visible end time reached the end of the day.
export function getParticipantEndTimeSyncResult(
  startTime,
  activityDurationMinutes,
  expectedParticipants,
  settings = {},
) {
  const endTime = getParticipantBasedEndTime(
    startTime,
    activityDurationMinutes,
    expectedParticipants,
  );
  const participantDurationMinutes = getParticipantDurationInMinutes(
    activityDurationMinutes,
    expectedParticipants,
  );

  if (endTime === null || participantDurationMinutes === null) {
    return {
      endTime,
      validation: {
        valid: true,
        reason: null,
        durationMinutes: null,
      },
    };
  }

  return {
    endTime,
    validation: validateDurationMinutes(participantDurationMinutes, settings),
  };
}

// Reverse of getParticipantBasedEndTime: how many whole Sessieduur-long sessions
// fit the start→end window. Floors (trailing minutes read as slack/break) and
// keeps the caller's end time as typed. Never drops below one session.
// Returns null only when inputs are missing/invalid (incl. end <= start, which
// the duration-range validation already surfaces) — i.e. never a silent no-op
// on an otherwise-valid user edit. See ADR-0007.
export function getParticipantCountFromTimes(
  startTime,
  endTime,
  activityDurationMinutes,
) {
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);
  const durationMinutes = Number(activityDurationMinutes);

  if (
    startMinutes === null ||
    endMinutes === null ||
    !Number.isFinite(durationMinutes) ||
    durationMinutes <= 0 ||
    endMinutes <= startMinutes
  ) {
    return null;
  }

  const count = Math.floor((endMinutes - startMinutes) / durationMinutes);
  return count >= 1 ? count : 1;
}

export function getMinimumEndTime(startTime, settings = null) {
  const startMinutes = parseTimeToMinutes(startTime);
  if (startMinutes === null) {
    return null;
  }

  const minDuration = Number(settings?.min_duration);
  const minDurationMinutes = Number.isFinite(minDuration) && minDuration > 0
    ? Math.round(minDuration * 60)
    : 0;

  return formatTimeFromMinutes(startMinutes + minDurationMinutes);
}

export function getTimeDurationInMinutes(startTime, endTime) {
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);

  if (startMinutes === null || endMinutes === null || endMinutes < startMinutes) {
    return null;
  }

  return endMinutes - startMinutes;
}

export function validateTimeDurationRange(times = {}, settings = {}) {
  const startMinutes = parseTimeToMinutes(times?.start_time);
  const endMinutes = parseTimeToMinutes(times?.end_time);

  if (startMinutes !== null && endMinutes !== null && endMinutes < startMinutes) {
    return {
      valid: false,
      reason: "range",
      durationMinutes: null,
    };
  }

  const durationMinutes = startMinutes !== null && endMinutes !== null
    ? endMinutes - startMinutes
    : null;

  if (durationMinutes === null) {
    return {
      valid: true,
      reason: null,
      durationMinutes: null,
    };
  }

  return validateDurationMinutes(durationMinutes, settings);
}

export function getTimeDurationValidationResultForValue(
  fieldMapping,
  value,
  times = {},
  settings = {},
) {
  const effectiveTimes = {
    ...(times ?? {}),
  };

  if (fieldMapping === START_TIME_MAPPING) {
    effectiveTimes.start_time = value;
  }

  if (fieldMapping === END_TIME_MAPPING) {
    effectiveTimes.end_time = value;
  }

  return validateTimeDurationRange(effectiveTimes, settings);
}

export function getShiftedEndTime(startTime, previousStartTime, endTime) {
  const startMinutes = parseTimeToMinutes(startTime);
  const previousStartMinutes = parseTimeToMinutes(previousStartTime);
  const endMinutes = parseTimeToMinutes(endTime);

  if (
    startMinutes === null ||
    previousStartMinutes === null ||
    endMinutes === null ||
    endMinutes < previousStartMinutes
  ) {
    return null;
  }

  return formatTimeFromMinutes(startMinutes + (endMinutes - previousStartMinutes));
}

export const CASCADE_TARGET_END_TIME = "end_time";
export const CASCADE_TARGET_EXPECTED_PARTICIPANTS = "expected_participants";

// Reconciles Verwacht aantal deelnemers against the window that was actually
// committed. Runs only when the window's *length* changed: a shift that keeps the
// duration leaves the count alone even if it never matched the window to begin
// with, so translating 10:00-16:30 to 09:00-15:30 can never move it. An unknown
// previous duration counts as changed, so first-time entry still derives a count.
function reconcileExpectedParticipants({
  previousTimes,
  nextTimes,
  expectedParticipants,
  sessionDurationMinutes,
  reason,
}) {
  const nextDuration = getTimeDurationInMinutes(
    nextTimes.start_time,
    nextTimes.end_time,
  );

  if (nextDuration === null) {
    return null;
  }

  const previousDuration = getTimeDurationInMinutes(
    previousTimes.start_time,
    previousTimes.end_time,
  );

  if (previousDuration !== null && previousDuration === nextDuration) {
    return null;
  }

  const nextCount = getParticipantCountFromTimes(
    nextTimes.start_time,
    nextTimes.end_time,
    sessionDurationMinutes,
  );

  if (nextCount === null || String(nextCount) === String(expectedParticipants ?? "")) {
    return null;
  }

  return {
    target: CASCADE_TARGET_EXPECTED_PARTICIPANTS,
    value: String(nextCount),
    reason,
  };
}

// Resolves every Afgeleide wijziging that follows one committed time/participant
// answer, in one pass over the values as they stand *before* the commit. Pure by
// design: nothing is read back mid-cascade, which is what previously produced a
// participant count for a window that never existed (ADR-0024).
//
// Returns `blocked` when the edit must be refused with a Veldvalidatie instead,
// and otherwise a list of writes, each carrying the melding reason for its field.
export function resolveTimeFieldCascade({
  fieldMapping,
  value,
  previousValue = null,
  times = {},
  expectedParticipants = null,
  sessionDurationMinutes = null,
  settings = {},
} = {}) {
  const currentStart = times?.start_time ?? null;
  const currentEnd = times?.end_time ?? null;
  const empty = { blocked: null, writes: [] };

  if (fieldMapping === EXPECTED_PARTICIPANTS_MAPPING) {
    const syncResult = getParticipantEndTimeSyncResult(
      currentStart,
      sessionDurationMinutes,
      value,
      settings,
    );

    if (syncResult?.validation && !syncResult.validation.valid) {
      return { blocked: syncResult.validation, writes: [] };
    }

    const nextEndTime = syncResult?.endTime ?? null;
    if (nextEndTime === null || nextEndTime === currentEnd) {
      return empty;
    }

    return {
      blocked: null,
      writes: [
        {
          target: CASCADE_TARGET_END_TIME,
          value: nextEndTime,
          reason: "end_time_from_participants",
        },
      ],
    };
  }

  if (fieldMapping === START_TIME_MAPPING) {
    const writes = [];
    const shiftedEndTime = getShiftedEndTime(value, previousValue, currentEnd);
    const nextEnd = shiftedEndTime === null ? currentEnd : shiftedEndTime;

    if (shiftedEndTime !== null && shiftedEndTime !== currentEnd) {
      writes.push({
        target: CASCADE_TARGET_END_TIME,
        value: shiftedEndTime,
        reason: "start_time",
      });
    }

    // Only reachable when the shift clamped at 23:59 and so shortened the window;
    // an exact translation is filtered out inside the reconcile helper.
    const participantsWrite = reconcileExpectedParticipants({
      previousTimes: { start_time: previousValue, end_time: currentEnd },
      nextTimes: { start_time: value, end_time: nextEnd },
      expectedParticipants,
      sessionDurationMinutes,
      reason: "participants_from_times",
    });

    if (participantsWrite) {
      writes.push(participantsWrite);
    }

    return { blocked: null, writes };
  }

  if (fieldMapping === END_TIME_MAPPING) {
    const participantsWrite = reconcileExpectedParticipants({
      previousTimes: { start_time: currentStart, end_time: previousValue },
      nextTimes: { start_time: currentStart, end_time: value },
      expectedParticipants,
      sessionDurationMinutes,
      reason: "participants",
    });

    return {
      blocked: null,
      writes: participantsWrite ? [participantsWrite] : [],
    };
  }

  return empty;
}

export function hasSameTimeDuration(originalTimes = {}, currentTimes = {}) {
  const originalDuration = getTimeDurationInMinutes(
    originalTimes.start_time,
    originalTimes.end_time,
  );
  const currentDuration = getTimeDurationInMinutes(
    currentTimes.start_time,
    currentTimes.end_time,
  );

  return originalDuration !== null && originalDuration === currentDuration;
}

export function isSameDurationTimeShift(originalTimes = {}, currentTimes = {}) {
  const originalStart = parseTimeToMinutes(originalTimes.start_time);
  const originalEnd = parseTimeToMinutes(originalTimes.end_time);
  const currentStart = parseTimeToMinutes(currentTimes.start_time);
  const currentEnd = parseTimeToMinutes(currentTimes.end_time);

  if (
    originalStart === null ||
    originalEnd === null ||
    currentStart === null ||
    currentEnd === null
  ) {
    return false;
  }

  const timeChanged = originalStart !== currentStart || originalEnd !== currentEnd;

  return timeChanged && hasSameTimeDuration(originalTimes, currentTimes);
}

export function getEffectiveTimeWijzigingsimpact(field, originalTimes, currentTimes) {
  const originalImpact = field?.wijzigingsimpact;

  const isOfferteImpact =
    typeof originalImpact === "string" &&
    originalImpact.startsWith("offerte_impact");

  if (!isOfferteImpact || !isEditableTimeField(field)) {
    return originalImpact;
  }

  return isSameDurationTimeShift(originalTimes, currentTimes)
    ? "wijzigingsverzoek"
    : originalImpact;
}

export function timeFieldKey(groupKey, activityKey, fieldId) {
  return `${groupKey ?? ""}:${activityKey ?? ""}:${fieldId}`;
}

export function getStoredTimeAnswer(intakeform, groupKey, activityKey, fieldId) {
  const fields = intakeform?.data?.fields;
  const group = fields?.[groupKey];
  const activity = group?.[activityKey];

  return activity?.[fieldId];
}

export function getOriginalTimeValue(intakeform, groupKey, activityKey, field) {
  const storedAnswer = getStoredTimeAnswer(
    intakeform,
    groupKey,
    activityKey,
    field.id,
  );

  return storedAnswer ?? field.answer ?? field.value ?? null;
}

export function collectOriginalTimeValues(intakeform) {
  const originals = {};
  const groups = intakeform?.grouped_fields?.groups || {};

  for (const [groupKey, group] of Object.entries(groups)) {
    const activities = group?.fields || {};

    for (const [activityKey, activity] of Object.entries(activities)) {
      const steps = Array.isArray(activity?.fields?.data)
        ? activity.fields.data
        : [];

      for (const step of steps) {
        const sections = Array.isArray(step?.sections) ? step.sections : [];

        for (const section of sections) {
          const fields = Array.isArray(section?.fields) ? section.fields : [];

          for (const field of fields) {
            if (isEditableTimeField(field)) {
              originals[timeFieldKey(groupKey, activityKey, field.id)] =
                getOriginalTimeValue(intakeform, groupKey, activityKey, field);
            }
          }
        }
      }
    }
  }

  return originals;
}

export function normalizeActivityDates(dates, now = new Date()) {
  if (!Array.isArray(dates)) {
    return [];
  }

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const uniqueDates = new Map();

  dates.forEach((item) => {
    const timestamp = Number(item?.date);
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
      return;
    }

    const date = new Date(timestamp * 1000);
    const calendarDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    if (calendarDate < today) {
      return;
    }

    const key = [
      calendarDate.getFullYear(),
      calendarDate.getMonth(),
      calendarDate.getDate(),
    ].join("-");

    if (!uniqueDates.has(key)) {
      uniqueDates.set(key, item);
    }
  });

  return [...uniqueDates.values()];
}

export function getActivityDates(intakeform, groupKey, activityKey) {
  const locationDates =
    intakeform?.locations?.groups?.[groupKey]?.activities?.[activityKey]?.dates;
  const groupedFieldDates =
    intakeform?.grouped_fields?.groups?.[groupKey]?.fields?.[activityKey]?.dates;

  const dates = Array.isArray(locationDates)
    ? locationDates
    : Array.isArray(groupedFieldDates)
      ? groupedFieldDates
      : [];

  return normalizeActivityDates(dates);
}

export function getTimeChangeDialogData(dates) {
  const validDates = normalizeActivityDates(dates);

  if (validDates.length === 0) {
    return { type: "multiple_dates", date: null };
  }

  if (validDates.length === 1) {
    return {
      type: "one_date",
      date: new Date(Number(validDates[0].date) * 1000),
    };
  }

  return null;
}

// De kalendergrenzen van de Dienstkiezer: de eerste en de laatste dienst die te
// kiezen is (ADR-0053).
//
// Er zijn hier geen month/year-props nodig om op de juiste maand te openen.
// VDatePicker bepaalt zijn openingsmaand als `model[0] ?? clampDate(today)`, en
// clampDate knijpt vandaag naar `min` zodra vandaag daarvoor ligt -- de min doet
// dus twee dingen tegelijk. De max is net zo hard nodig: VDatePickerYears bouwt
// zijn jarenlijst als `jaar-100 .. jaar+52` en klemt die alleen met min/max,
// wat bij een offerte in 2050 de jaren 2026-2078 opleverde (ClickUp 86cb8819t).
//
// Min/max over de hele set, nooit dates[0] en dates[length-1]: de query erachter
// (IntakeFormRepository::getActivityDates) heeft geen ORDER BY en joint over
// activity_location_specialists, dus bij twee specialisten komen de rijen per
// specialist terug in plaats van op datum.
//
// De grenzen beslaan hele dagen omdat calendar.js een dagcel er rechtstreeks
// tegen afzet -- `isBefore(endOfDay(cel), min)` en `isAfter(cel, max)`. Deze
// rijen dragen een tijdstip midden op de dag, en zo'n grens zou de eerste of de
// laatste dienst net aan de verkeerde kant zetten.
export function getDienstBounds(dates, now = new Date()) {
  const timestamps = normalizeActivityDates(dates, now)
    .map((item) => Number(item?.date))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (timestamps.length === 0) {
    return { min: null, max: null };
  }

  const first = new Date(Math.min(...timestamps) * 1000);
  const last = new Date(Math.max(...timestamps) * 1000);

  return {
    min: new Date(first.getFullYear(), first.getMonth(), first.getDate()),
    max: new Date(
      last.getFullYear(),
      last.getMonth(),
      last.getDate(),
      23,
      59,
      59,
      999,
    ),
  };
}

export function createPendingTimeChange(intakeform, originalTimeValues, data) {
  if (!isEditableTimeField(data) || !data.key || !data.group_key) {
    return undefined;
  }

  const key = timeFieldKey(data.group_key, data.key, data.id);
  const originalValue = originalTimeValues[key];

  if (data.value === originalValue) {
    return null;
  }

  return {
    date: null,
    dates: getActivityDates(intakeform, data.group_key, data.key),
    fieldId: data.id,
    groupKey: data.group_key,
    key: data.key,
    originalValue,
    value: data.value,
    // The Afgeleide wijzigingen this edit is about to make (the deelnemers an
    // end time implies) as they stand *before* it, so cancelling the Datumscope
    // takes those back too (ClickUp 1240ccekgan).
    derivedOriginals: collectDerivedOriginals(intakeform, data.group_key, data.key),
  };
}

// While a time change is pending, a second edit makes a new pending change; it
// must keep the first one's derivedOriginals, or a cancel restores a value the
// first edit had already derived.
export function nextPendingTimeChange(current, next) {
  if (!next || !current?.derivedOriginals) {
    return next;
  }

  return { ...next, derivedOriginals: current.derivedOriginals };
}

function activityFieldsOf(intakeform, groupKey, key) {
  const activity = intakeform?.grouped_fields?.groups?.[groupKey]?.fields?.[key];
  const steps = Array.isArray(activity?.fields?.data) ? activity.fields.data : [];
  const fields = [];

  for (const step of steps) {
    for (const section of Array.isArray(step?.sections) ? step.sections : []) {
      fields.push(...(Array.isArray(section?.fields) ? section.fields : []));
    }
  }

  return fields;
}

function collectDerivedOriginals(intakeform, groupKey, key) {
  const answers = intakeform?.data?.fields?.[groupKey]?.[key] ?? {};
  const originals = {};

  activityFieldsOf(intakeform, groupKey, key).forEach((field) => {
    if (field?.field_mapping === EXPECTED_PARTICIPANTS_MAPPING && field.id !== undefined) {
      originals[field.id] = Object.prototype.hasOwnProperty.call(answers, field.id)
        ? answers[field.id]
        : null;
    }
  });

  return originals;
}

export function resetActivityTimeAnswerValues(intakeform, originalTimeValues, change) {
  if (!change?.groupKey || !change?.key) {
    return;
  }

  Object.entries(change.derivedOriginals || {}).forEach(([fieldId, value]) => {
    setTimeAnswerValue(intakeform, { ...change, fieldId }, value);
  });

  const activity =
    intakeform?.grouped_fields?.groups?.[change.groupKey]?.fields?.[change.key];
  const steps = Array.isArray(activity?.fields?.data)
    ? activity.fields.data
    : [];

  for (const step of steps) {
    const sections = Array.isArray(step?.sections) ? step.sections : [];

    for (const section of sections) {
      const fields = Array.isArray(section?.fields) ? section.fields : [];

      for (const field of fields) {
        if (isEditableTimeField(field)) {
          const key = timeFieldKey(change.groupKey, change.key, field.id);
          const value = Object.prototype.hasOwnProperty.call(
            originalTimeValues || {},
            key,
          )
            ? originalTimeValues[key]
            : field.answer ?? field.value ?? null;

          setTimeAnswerValue(
            intakeform,
            { ...change, fieldId: field.id },
            value,
          );
        }
      }
    }
  }
}

export function setTimeAnswerValue(intakeform, change, value) {
  if (!intakeform?.data?.fields?.[change.groupKey]?.[change.key]) {
    return;
  }

  intakeform.data.fields[change.groupKey][change.key][change.fieldId] = value;
}

export function createEditedTimeFields(change, dialogData) {
  return {
    activity_id: change.key,
    type: dialogData.type,
    date: dialogData.date,
  };
}
